import React, {useEffect, useState} from 'react';
import {
    Container,
    Typography,
    Box,
    Stack,
    TextField, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Button
} from '@mui/material';
import {useAppSelector, useAppDispatch} from '../../app/hooks';
import {
    actionsCell,
    editButton, editIcon,
    searchField,
    searchInner,
    tableCell,
    tableContainer,
    tableHeaderCell
} from "./usersStyles";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import TablesPagination from "../../components/TablesPagination/TablesPagination";
import {createGetUsersQuery} from "../../helpers/createGetUsersQuery";
import { useSortingAndSearch } from './hooks/useSortingAndSearch';
import UserForm from "./components/UserForm";
import {getUsers, IUser, setItemsPerPage, setPageNumber} from "./usersSlice";


interface ITableHeaders {
    id: keyof IUser;
    label: string
}

const tableHeaders: ITableHeaders[] = [
    {id: 'name', label: 'Name'},
    {id: 'socialMedia', label: 'Social Media'},
    {id: 'contact', label: 'Contact'}
];


const UsersPage: React.FC = () => {
    const [isOpenForm, setIsOpenForm] = useState<boolean>(false);
    const [editingUser, setEditingUser] = useState<IUser | null>(null);
    const {sorting, searchValue, setSearchValue, handleSorting} = useSortingAndSearch();
    const dispatch = useAppDispatch();
    const {users, totalCount, pageNumber, itemsPerPage} = useAppSelector((state) => state.users);


    useEffect(() => {
        const queryParams = createGetUsersQuery(
            pageNumber,
            itemsPerPage,
            sorting,
            searchValue
        )
        dispatch(getUsers(queryParams));
    }, []);


    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage: number) => {
        dispatch(setPageNumber(newPage))

        const queryParams = createGetUsersQuery(
            newPage + 1,
            itemsPerPage,
            sorting,
            searchValue
        )
        dispatch(getUsers(queryParams));
    }

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setPageNumber(0));
        dispatch(setItemsPerPage(+event.target.value))

        const queryParams = createGetUsersQuery(
            1,
            +event.target.value,
            sorting,
            searchValue
        )
        dispatch(getUsers(queryParams));
    }

    const handleEditUser = (currentUser: IUser) => {
        setEditingUser(currentUser);
        setIsOpenForm(true);
    }

    const onCloseForm = () => {
        setEditingUser(null);
        setIsOpenForm(false);
    }

    return (
        <Container sx={{mt: 4}}>
            {isOpenForm &&
                <UserForm
                    editingUser={editingUser}
                    onCloseForm={onCloseForm}
                />
            }

            <Typography variant="h4" gutterBottom>
                Users
            </Typography>
            <Box sx={{mt: 4}}>
                <Box sx={searchInner}>
                    <Stack spacing={2} sx={{width: 300}}>
                        <TextField
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            label="Search"
                            sx={searchField}
                        />
                    </Stack>
                </Box>
                <Paper sx={{width: '100%', overflow: 'hidden'}}>
                    <TableContainer sx={tableContainer}>
                        <Table stickyHeader aria-label="sticky table">
                            <TableHead>
                                <TableRow>
                                    {tableHeaders.map(header => (
                                        <TableCell
                                            sx={(theme) => ({
                                                backgroundColor: theme.palette.mode === 'dark' ? "#3e3c3c" : "#f0f0f0",
                                                ...tableHeaderCell,
                                                '&:hover': {
                                                    backgroundColor:
                                                        theme.palette.mode === 'dark' ? '#90caf9' : '#9cbed9',
                                                },
                                            })}
                                            key={header.id}
                                            onClick={() => handleSorting(header.id)}
                                        >
                                            {header.label}
                                        </TableCell>
                                    ))}
                                    <TableCell
                                        sx={(theme) => ({
                                            backgroundColor: theme.palette.mode === 'dark' ? "#3e3c3c" : "#f0f0f0",
                                            ...actionsCell
                                        })}
                                    >
                                        Actions
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {users.map(user => {
                                    return (
                                        <TableRow key={user.id}>
                                            <TableCell sx={{...tableCell}}>{user.name}</TableCell>
                                            <TableCell sx={{...tableCell}}>
                                                {
                                                    user.socialMedia &&
                                                    <Box>
                                                        {user.socialMedia.facebook &&
                                                            <p>facebook: {user.socialMedia.facebook}</p>
                                                        }
                                                        {user.socialMedia.linkedIn &&
                                                            <p>linkedIn: {user.socialMedia.linkedIn}</p>
                                                        }
                                                        {user.socialMedia.comments &&
                                                            <p>comments: {user.socialMedia.comments}</p>
                                                        }
                                                    </Box>
                                                }
                                            </TableCell>
                                            <TableCell sx={{...tableCell}}>
                                                {
                                                    user.contact &&
                                                    <Box>
                                                        <p>
                                                            {user.contact.country}
                                                            {user.contact.hometown && `,${user.contact.hometown}`}
                                                        </p>
                                                        <p>{user.contact.telephon}</p>
                                                        <p>{user.contact.email}</p>
                                                    </Box>
                                                }
                                            </TableCell>

                                            <TableCell sx={{...tableCell, textAlign: "center"}}
                                            >
                                                <Button
                                                    sx={editButton}
                                                    onClick={() => handleEditUser(user)}
                                                >
                                                    <BorderColorIcon sx={editIcon}/>
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablesPagination
                        totalCount={totalCount}
                        itemsPerPage={itemsPerPage}
                        pageNumber={pageNumber}
                        handleChangePage={handleChangePage}
                        handleChangeRowsPerPage={handleChangeRowsPerPage}
                    />
                </Paper>
            </Box>
        </Container>
    );
};

export default UsersPage;
